import { useMemo } from 'react';
import { useFormContext } from '../../../contexts/useFormContext';
import { getWorkflowSectionKeys } from '../../../types';
import { ProgressBar } from '@tw/tw-component-library';

interface ExtendedProgressBarProps {
  value?: number;
  total?: number;
  completed?: number;
  sectionCompleted?: boolean;
  actions?: Array<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: string;
  }>;
  isRejected?: boolean;
}

export const FormProgress = () => {
  const {
    sectionProgress,
    formState,
    setCurrentSection,
    canProceedToSection,
    getCurrentSectionFlow,
  } = useFormContext();

  // Get current section flow
  const currentSectionFlow = useMemo(() => {
    if (getCurrentSectionFlow) {
      return getCurrentSectionFlow();
    }

    // Use workflow-specific section keys instead of the generic getSectionFlow
    return getWorkflowSectionKeys(formState.workflowType);
  }, [getCurrentSectionFlow, formState.workflowType]);

  const progressActions = useMemo(() => {
    const currentSectionIndex = currentSectionFlow.indexOf(
      formState.currentSection
    );

    const baseActions = [
      {
        label: 'Previous Section',
        onClick: () =>
          setCurrentSection(currentSectionFlow[currentSectionIndex - 1]),
        disabled: currentSectionIndex === 0,
        variant: 'secondary',
      },
      {
        label: 'Next Section',
        onClick: () =>
          setCurrentSection(currentSectionFlow[currentSectionIndex + 1]),
        disabled:
          currentSectionIndex === currentSectionFlow.length - 1 ||
          !canProceedToSection(currentSectionFlow[currentSectionIndex + 1]),
        variant: 'primary',
      },
    ];

    return baseActions;
  }, [
    formState.currentSection,
    canProceedToSection,
    setCurrentSection,
    currentSectionFlow,
  ]);

  return (
    <ProgressBar
      {...({
        value: sectionProgress.percentage,
        total: sectionProgress.total,
        completed: sectionProgress.completed,
        sectionCompleted: sectionProgress.currentSectionComplete,
        actions: progressActions,
        isRejected: formState.isRejected,
      } as ExtendedProgressBarProps)}
    />
  );
};
